/**
 * FirmTemplateResolutionPanel — shows how every template category resolves
 * for a firm: firm-scoped overrides, inherited globals, and unresolved gaps.
 * Also includes a slim branding-packet trigger.
 */
import {
  Building2,
  ChevronRight,
  Download,
  Eye,
  FileText,
  Globe,
  Loader2,
  XCircle,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { AttorneySelect } from '@/components/matter/AttorneySelect';
import {
  TEMPLATE_CATEGORY_LABELS,
  TEMPLATE_FORMAT_COLORS,
  TEMPLATE_FORMAT_LABELS,
} from '@/components/templates-v2/constants';
import { PdfRenderer } from '@/components/templates-v2/document-generation/pdf/renderer/PdfFormRenderer';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type { CategoryResolution } from '@/services/api/templatesV2ServiceEnhanced';
import {
  useTemplateBrandingPacketStatusQuery,
  useTemplateGenerateBrandingPacketMutation,
  useTemplateRenderBrandedSampleMutation,
  useTemplateResolveAllQuery,
} from '@/services/api/templatesV2ServiceEnhanced';

interface FirmTemplateResolutionPanelProps {
  firmId: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Case-insensitive source_tier check — backend returns lowercase. */
const isGlobalTier = (tier: string): boolean =>
  tier.toLowerCase() === 'global';

const isFirmTier = (tier: string): boolean => tier.toLowerCase() === 'firm';

const SOURCE_TIER_CONFIG: Record<
  string,
  {
    label: string;
    icon: typeof Globe;
    color: string;
    rowBg: string;
    borderColor: string;
  }
> = {
  firm: {
    label: 'firm override',
    icon: Building2,
    color: 'text-emerald-600 dark:text-emerald-400',
    rowBg: 'bg-emerald-50/40 dark:bg-emerald-950/20',
    borderColor: 'border-emerald-100 dark:border-emerald-900/30',
  },
  global: {
    label: 'inherits global',
    icon: Globe,
    color: 'text-blue-500',
    rowBg: 'bg-blue-50/40 dark:bg-blue-950/20',
    borderColor: 'border-blue-100 dark:border-blue-900/30',
  },
  fallback: {
    label: 'fallback',
    icon: Globe,
    color: 'text-amber-500',
    rowBg: 'bg-amber-50/40 dark:bg-amber-950/20',
    borderColor: 'border-amber-100 dark:border-amber-900/30',
  },
  none: {
    label: 'no template',
    icon: XCircle,
    color: 'text-zinc-500',
    rowBg: 'bg-zinc-50/40 dark:bg-zinc-950/20',
    borderColor: 'border-zinc-200 dark:border-zinc-800',
  },
};

const getTierConfig = (tier: string) =>
  SOURCE_TIER_CONFIG[tier.toLowerCase()] ?? SOURCE_TIER_CONFIG.none;

// ---------------------------------------------------------------------------
// Resolution row — shows a single category resolution
// ---------------------------------------------------------------------------

const ResolutionRow = ({
  resolution,
  firmId,
}: {
  resolution: CategoryResolution;
  firmId: string;
}) => {
  const navigate = useNavigate();
  const label =
    TEMPLATE_CATEGORY_LABELS[resolution.category] ?? resolution.category;
  const { template } = resolution;
  const tierConfig = getTierConfig(resolution.source_tier);
  const TierIcon = tierConfig.icon;

  const [renderBranded, { isLoading: isRendering }] =
    useTemplateRenderBrandedSampleMutation();
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handlePreview = useCallback(async () => {
    if (!template) return;
    try {
      const blob = await renderBranded({
        templateId: template.id as string,
        firmId,
      }).unwrap();
      setPreviewBlob(blob);
      setDialogOpen(true);
    } catch {
      toast.error(`Failed to preview ${label}`);
    }
  }, [template, firmId, label, renderBranded]);

  const handleNavigate = useCallback(() => {
    if (!template) return;
    navigate(`/templates-v2/${template.id}?firmContext=${firmId}`);
  }, [template, firmId, navigate]);

  const formatLabel = template
    ? (TEMPLATE_FORMAT_LABELS[template.format] ?? template.format)
    : null;
  const formatColor = template
    ? (TEMPLATE_FORMAT_COLORS[template.format] ?? 'bg-zinc-100 text-zinc-600')
    : '';

  const canPreview =
    template && !['EXTERNAL', 'HTML', 'SMS'].includes(template.format);

  return (
    <>
      <tr
        className={cn(
          'group border-b',
          tierConfig.borderColor,
          tierConfig.rowBg,
        )}
      >
        <td colSpan={2} className="px-3 py-1.5">
          <div className="flex items-center gap-2">
            <TierIcon
              className={cn('h-3.5 w-3.5 shrink-0', tierConfig.color)}
            />
            <span
              className={cn(
                'text-xs font-medium',
                template
                  ? 'text-zinc-800 dark:text-zinc-200'
                  : 'text-zinc-500 dark:text-zinc-400',
              )}
            >
              {label}
            </span>
            <span
              className={cn('text-[10px]', tierConfig.color, 'opacity-70')}
            >
              {tierConfig.label}
            </span>
            {formatLabel && (
              <span
                className={cn(
                  'ml-1 inline-flex rounded px-1 py-px text-[10px] font-medium leading-tight',
                  formatColor,
                )}
              >
                {formatLabel}
              </span>
            )}
            {template && (
              <span className="ml-auto text-[10px] text-zinc-500 dark:text-zinc-400">
                {template.display_name || template.name}
              </span>
            )}
          </div>
        </td>
        <td className="px-2 py-1.5 text-right">
          <div className="flex items-center justify-end gap-1 opacity-0 transition-opacity group-hover:opacity-100">
            {canPreview && (
              <button
                type="button"
                onClick={handlePreview}
                disabled={isRendering}
                className={cn(
                  'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium',
                  tierConfig.color,
                  'hover:bg-zinc-100 disabled:opacity-40 dark:hover:bg-zinc-800/40',
                )}
                title="Preview branded sample"
              >
                {isRendering ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Eye className="h-3 w-3" />
                )}
                Preview
              </button>
            )}
            {template && (
              <button
                type="button"
                onClick={handleNavigate}
                className={cn(
                  'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium',
                  tierConfig.color,
                  'hover:bg-zinc-100 dark:hover:bg-zinc-800/40',
                )}
                title="Open template"
              >
                <FileText className="h-3 w-3" />
                Open
              </button>
            )}
          </div>
        </td>
      </tr>

      {/* Branded preview dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="flex max-h-screen max-w-4xl flex-col p-0">
          <DialogHeader className="flex-none border-b border-zinc-200 px-4 py-3 dark:border-zinc-700">
            <DialogTitle className="text-sm font-semibold">
              {label} — Branded Preview
            </DialogTitle>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto bg-zinc-50 dark:bg-zinc-900/50">
            {previewBlob ? (
              <PdfRenderer file={previewBlob} renderWidth={700} />
            ) : (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ---------------------------------------------------------------------------
// Branding Packet — compact inline trigger
// ---------------------------------------------------------------------------

const BrandingPacketInline = ({ firmId }: { firmId: string }) => {
  const [generatePacket, { isLoading: isStarting }] =
    useTemplateGenerateBrandingPacketMutation();

  const [attorneyId, setAttorneyId] = useState<string | null>(null);
  const [polling, setPolling] = useState(false);
  const [workflowTriggered, setWorkflowTriggered] = useState(false);

  const { data: status } = useTemplateBrandingPacketStatusQuery(
    { firmId },
    { skip: !workflowTriggered, pollingInterval: polling ? 3000 : 0 },
  );

  useEffect(() => {
    if (!status) return;
    if (status.status === 'running') {
      setPolling(true);
    } else if (status.status === 'completed' || status.status === 'failed') {
      setPolling(false);
    }
  }, [status]);

  const handleGenerate = useCallback(async () => {
    try {
      await generatePacket({ firmId, attorneyId }).unwrap();
      setWorkflowTriggered(true);
      setPolling(true);
      toast.success('Branding packet generation started');
    } catch {
      toast.error('Failed to start branding packet generation');
    }
  }, [firmId, attorneyId, generatePacket]);

  const isRunning = polling || isStarting;

  return (
    <div className="flex items-center gap-2">
      {/* Status / download links */}
      {status?.status === 'running' && (
        <span className="flex items-center gap-1 text-xs text-zinc-500">
          <Loader2 className="h-3 w-3 animate-spin" />
          Generating...
        </span>
      )}
      {status?.status === 'completed' && (
        <>
          {status.compiled_pdf_download_url && (
            <a
              href={status.compiled_pdf_download_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 rounded border border-zinc-200 px-2 py-0.5 text-[11px] font-medium text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
            >
              <FileText className="h-3 w-3" />
              PDF
            </a>
          )}
          {status.download_url && (
            <a
              href={status.download_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 rounded border border-zinc-200 px-2 py-0.5 text-[11px] font-medium text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
            >
              <Download className="h-3 w-3" />
              ZIP
            </a>
          )}
        </>
      )}
      {status?.status === 'failed' && (
        <span className="text-[11px] font-medium text-red-600">Failed</span>
      )}

      {/* Generate button with attorney popover */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            size="sm"
            variant="outline"
            disabled={isRunning}
            className="h-7 gap-1 px-2 text-xs"
          >
            {isRunning ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Download className="h-3 w-3" />
            )}
            Branding Packet
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-3" align="end">
          <p className="mb-2 text-xs text-zinc-500">
            Generate a compiled PDF of all branded templates
          </p>
          <div className="mb-2">
            <AttorneySelect
              firmId={firmId}
              value={attorneyId}
              onChange={setAttorneyId}
              placeholder="Auto-select attorney"
              disabled={isRunning}
            />
          </div>
          <Button
            size="sm"
            className="w-full gap-1.5"
            onClick={handleGenerate}
            disabled={isRunning}
          >
            {isRunning ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Download className="h-3.5 w-3.5" />
            )}
            {isRunning ? 'Generating...' : 'Generate'}
          </Button>
        </PopoverContent>
      </Popover>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main panel — collapsible, collapsed by default
// ---------------------------------------------------------------------------

export const FirmTemplateResolutionPanel = ({
  firmId,
}: FirmTemplateResolutionPanelProps) => {
  const {
    data: resolveData,
    isLoading,
    isError,
  } = useTemplateResolveAllQuery({ firmId }, { skip: !firmId });

  if (!firmId) return null;

  // Show ALL resolutions so the user can see how each category resolves
  const allResolutions = resolveData?.resolutions?.filter(
    (res) => res.template || res.source_tier.toLowerCase() === 'none',
  );

  const firmCount =
    allResolutions?.filter((r) => isFirmTier(r.source_tier)).length ?? 0;
  const globalCount =
    allResolutions?.filter((r) => isGlobalTier(r.source_tier)).length ?? 0;
  const totalCount = allResolutions?.length ?? 0;

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-zinc-200 bg-white px-3 py-2 dark:border-zinc-800 dark:bg-zinc-900">
        <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
        <span className="text-xs text-zinc-500">
          Loading template resolution...
        </span>
      </div>
    );
  }

  if (isError || !allResolutions) return null;

  if (totalCount === 0) return null;

  return (
    <Collapsible defaultOpen={false}>
      <div className="rounded-md border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900">
        {/* Collapsible header */}
        <div className="flex items-center justify-between px-3 py-2">
          <CollapsibleTrigger className="group flex items-center gap-2 text-left">
            <ChevronRight className="h-3.5 w-3.5 text-zinc-400 transition-transform group-data-[state=open]:rotate-90" />
            <span className="text-xs font-medium text-zinc-700 dark:text-zinc-300">
              Template Resolution
            </span>
            <span className="flex items-center gap-1.5 text-[10px] text-zinc-500">
              {firmCount > 0 && (
                <span className="flex items-center gap-0.5">
                  <Building2 className="h-3 w-3 text-emerald-500" />
                  {firmCount} firm
                </span>
              )}
              {globalCount > 0 && (
                <span className="flex items-center gap-0.5">
                  <Globe className="h-3 w-3 text-blue-500" />
                  {globalCount} global
                </span>
              )}
            </span>
          </CollapsibleTrigger>
          <BrandingPacketInline firmId={firmId} />
        </div>

        {/* Collapsible body — all resolution rows */}
        <CollapsibleContent>
          <div className="border-t border-zinc-100 dark:border-zinc-800">
            <table className="w-full">
              <tbody>
                {allResolutions.map((res) => (
                  <ResolutionRow
                    key={res.category}
                    resolution={res}
                    firmId={firmId}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};
